import { useEffect, useRef, useState } from "react";

const RECAPTCHA_SRC = "https://www.google.com/recaptcha/api.js?render=explicit";
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

/**
 * Small wrapper around Google reCAPTCHA v2 "I'm not a robot" checkbox.
 * If SITE_KEY is empty, this renders nothing and always reports as "verified"
 * so the app works out of the box before real keys are configured.
 *
 * onChange(token: string | null)  — called when the user completes or expires the challenge.
 */
export default function Recaptcha({ onChange, theme = "light" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!SITE_KEY) {
      // No key configured — skip captcha entirely (bypass mode).
      onChange?.("__bypass__");
      return;
    }
    let cancelled = false;

    const loadScript = () =>
      new Promise((resolve, reject) => {
        if (window.grecaptcha && window.grecaptcha.render) return resolve();
        const existing = document.querySelector(`script[src^="${RECAPTCHA_SRC}"]`);
        if (existing) {
          existing.addEventListener("load", resolve);
          existing.addEventListener("error", reject);
          return;
        }
        const s = document.createElement("script");
        s.src = RECAPTCHA_SRC;
        s.async = true;
        s.defer = true;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });

    (async () => {
      try {
        await loadScript();
        // grecaptcha.ready waits until API is fully initialized.
        window.grecaptcha.ready(() => {
          if (cancelled || !containerRef.current) return;
          if (widgetIdRef.current !== null) return;
          widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
            sitekey: SITE_KEY,
            theme,
            callback: (token) => onChange?.(token),
            "expired-callback": () => onChange?.(null),
            "error-callback": () => onChange?.(null),
          });
          setReady(true);
        });
      } catch {
        // Fail-open: if reCAPTCHA fails to load, still allow submissions.
        onChange?.("__bypass__");
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!SITE_KEY) return null;
  return (
    <div className="min-h-[78px]">
      <div ref={containerRef} data-testid="recaptcha-widget" />
      {!ready && (
        <div className="text-xs text-[#94A0AE]" data-testid="recaptcha-loading">
          Loading verification…
        </div>
      )}
    </div>
  );
}

export const RECAPTCHA_ENABLED = Boolean(SITE_KEY);
